import React, { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { getContrastColor } from './common/utils';

function MonthlyCalendar({
    year, month, schedules, subjects,
    onTodoAdd, onTodoToggle, onTodoContextMenu, onTodoDoubleClick,
    onScheduleContextMenu, onScheduleDoubleClick,
    onTodoDrop,
    targetUserId, startWithMonday, t
}) {
    const today = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d.getTime();
    }, []);

    // Mouse drag state (HTML5 drag API - desktop only)
    const [dragOverDate, setDragOverDate] = useState(null);
    const dragItemRef = useRef(null);

    // Touch long-press / drag state
    const touchTimerRef = useRef(null);
    const touchStartPosRef = useRef(null);
    const touchDragItemRef = useRef(null);
    const [touchDragging, setTouchDragging] = useState(false);
    const touchDragElRef = useRef(null); // the original element being dragged

    // Cleanup touch timer on unmount
    useEffect(() => {
        return () => {
            if (touchTimerRef.current) clearTimeout(touchTimerRef.current);
        };
    }, []);

    // Calculate calendar grid (5-6 weeks)
    const calendarWeeks = useMemo(() => {
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        let startDayOfWeek = firstDay.getDay();
        if (startWithMonday) {
            startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
        }

        const calendarStart = new Date(firstDay);
        calendarStart.setDate(calendarStart.getDate() - startDayOfWeek);

        const weeks = [];
        const current = new Date(calendarStart);

        while (current <= lastDay || weeks.length < 5) {
            const week = [];
            for (let i = 0; i < 7; i++) {
                week.push(new Date(current));
                current.setDate(current.getDate() + 1);
            }
            weeks.push(week);
            if (weeks.length >= 6) break;
        }

        return weeks;
    }, [year, month, startWithMonday]);

    // Group schedules by date string
    const schedulesByDate = useMemo(() => {
        const map = {};
        if (!schedules) return map;
        schedules.forEach(schedule => {
            const d = new Date(schedule.start_time);
            const dateKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
            if (!map[dateKey]) map[dateKey] = { todos: [], timed: [] };
            const isTodo = schedule.schedule_type === 'todo' || schedule.scheduled_time === 0;
            if (isTodo) {
                map[dateKey].todos.push(schedule);
            } else {
                map[dateKey].timed.push(schedule);
            }
        });
        return map;
    }, [schedules]);

    // Day headers
    const dayHeaders = useMemo(() => {
        const days = startWithMonday
            ? ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
            : ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        return days.map(d => t(d));
    }, [startWithMonday, t]);

    const dayColors = useMemo(() => {
        if (startWithMonday) {
            return ['black', 'black', 'black', 'black', 'black', 'blue', 'red'];
        }
        return ['red', 'black', 'black', 'black', 'black', 'black', 'blue'];
    }, [startWithMonday]);

    // === Mouse drag handlers (desktop HTML5 drag) ===
    const handleTodoDragStart = useCallback((e, schedule) => {
        dragItemRef.current = schedule;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', schedule.schedule_id);
        e.currentTarget.style.opacity = '0.5';
    }, []);

    const handleTodoDragEnd = useCallback((e) => {
        e.currentTarget.style.opacity = '1';
        dragItemRef.current = null;
        setDragOverDate(null);
    }, []);

    const handleCellDragOver = useCallback((e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    }, []);

    const handleCellDragEnter = useCallback((dateKey) => {
        setDragOverDate(dateKey);
    }, []);

    const handleCellDragLeave = useCallback(() => {
        setDragOverDate(null);
    }, []);

    const handleCellDrop = useCallback((e, date) => {
        e.preventDefault();
        setDragOverDate(null);
        if (dragItemRef.current && onTodoDrop) {
            onTodoDrop(dragItemRef.current, date);
            dragItemRef.current = null;
        }
    }, [onTodoDrop]);

    // === Touch handlers: long-press for context menu + touch drag ===
    const createFakeEvent = (touch, schedule) => ({
        preventDefault: () => {},
        stopPropagation: () => {},
        clientX: touch.clientX,
        clientY: touch.clientY
    });

    const handleItemTouchStart = useCallback((e, schedule, type) => {
        const touch = e.touches[0];
        touchStartPosRef.current = { x: touch.clientX, y: touch.clientY, schedule, type };
        touchDragItemRef.current = null;

        touchTimerRef.current = setTimeout(() => {
            touchTimerRef.current = null;
            // Long-press detected
            if (type === 'todo' && !targetUserId) {
                // Start touch drag mode for TODO
                touchDragItemRef.current = schedule;
                touchDragElRef.current = e.target.closest('.monthly-schedule-item');
                if (touchDragElRef.current) touchDragElRef.current.style.opacity = '0.5';
                setTouchDragging(true);
            } else {
                // Open context menu for both types
                const fakeEvent = createFakeEvent(touch, schedule);
                if (type === 'todo' && onTodoContextMenu) {
                    onTodoContextMenu(fakeEvent, schedule);
                } else if (type === 'timed' && onScheduleContextMenu) {
                    onScheduleContextMenu(fakeEvent, schedule);
                }
            }
        }, 400);
    }, [targetUserId, onTodoContextMenu, onScheduleContextMenu]);

    const handleItemTouchMove = useCallback((e) => {
        const touch = e.touches[0];
        if (touchTimerRef.current && touchStartPosRef.current) {
            const dist = Math.sqrt(
                Math.pow(touch.clientX - touchStartPosRef.current.x, 2) +
                Math.pow(touch.clientY - touchStartPosRef.current.y, 2)
            );
            if (dist > 10) {
                clearTimeout(touchTimerRef.current);
                touchTimerRef.current = null;
            }
        }

        // Touch drag in progress
        if (touchDragItemRef.current) {
            if (e.cancelable) e.preventDefault();
            // Find cell under finger
            const el = document.elementFromPoint(touch.clientX, touch.clientY);
            const cell = el?.closest('.monthly-cell');
            if (cell) {
                const dateKey = cell.getAttribute('data-datekey');
                setDragOverDate(dateKey);
            } else {
                setDragOverDate(null);
            }
        }
    }, []);

    const handleItemTouchEnd = useCallback((e) => {
        if (touchTimerRef.current) {
            clearTimeout(touchTimerRef.current);
            touchTimerRef.current = null;
        }

        // Complete touch drag
        if (touchDragItemRef.current && dragOverDate) {
            // Find the date for this dateKey
            const parts = dragOverDate.split('-').map(Number);
            const targetDate = new Date(parts[0], parts[1], parts[2]);
            if (onTodoDrop) {
                onTodoDrop(touchDragItemRef.current, targetDate);
            }
        }

        // Reset
        if (touchDragElRef.current) {
            touchDragElRef.current.style.opacity = '1';
            touchDragElRef.current = null;
        }
        touchDragItemRef.current = null;
        touchStartPosRef.current = null;
        setTouchDragging(false);
        setDragOverDate(null);
    }, [dragOverDate, onTodoDrop]);

    // Global touch move/end for drag (needs passive: false)
    useEffect(() => {
        if (!touchDragging) return;
        const onMove = (e) => handleItemTouchMove(e);
        const onEnd = (e) => handleItemTouchEnd(e);
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('touchend', onEnd);
        return () => {
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onEnd);
        };
    }, [touchDragging, handleItemTouchMove, handleItemTouchEnd]);

    return (
        <div className="monthly-calendar">
            <table className="monthly-table">
                <thead>
                    <tr>
                        {dayHeaders.map((day, i) => (
                            <th key={i} style={{ color: dayColors[i] }}>{day}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {calendarWeeks.map((week, weekIdx) => (
                        <tr key={weekIdx}>
                            {week.map((date, dayIdx) => {
                                const isCurrentMonth = date.getMonth() === month;
                                const dateCleared = new Date(date);
                                dateCleared.setHours(0, 0, 0, 0);
                                const isToday = dateCleared.getTime() === today;
                                const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
                                const daySchedules = schedulesByDate[dateKey] || { todos: [], timed: [] };
                                const { todos, timed } = daySchedules;
                                const hasBoth = todos.length > 0 && timed.length > 0;
                                const isDragOver = dragOverDate === dateKey;

                                let dayColor = 'black';
                                if (date.getDay() === 0) dayColor = 'red';
                                if (date.getDay() === 6) dayColor = 'blue';

                                return (
                                    <td
                                        key={dayIdx}
                                        data-datekey={dateKey}
                                        className={`monthly-cell ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'monthly-today' : ''} ${isDragOver ? 'monthly-drag-over' : ''}`}
                                        onDragOver={handleCellDragOver}
                                        onDragEnter={() => handleCellDragEnter(dateKey)}
                                        onDragLeave={handleCellDragLeave}
                                        onDrop={(e) => handleCellDrop(e, date)}
                                    >
                                        <div className="monthly-cell-header">
                                            <span
                                                className={`monthly-date-number ${isToday ? 'today-badge' : ''}`}
                                                style={{ color: isCurrentMonth ? dayColor : '#bbb' }}
                                            >
                                                {date.getDate()}
                                            </span>
                                            {!targetUserId && isCurrentMonth && (
                                                <button
                                                    className="monthly-add-btn"
                                                    onClick={() => onTodoAdd(date)}
                                                    title={t('todo.add')}
                                                >+</button>
                                            )}
                                        </div>
                                        <div className="monthly-cell-items">
                                            {/* TODO 섹션 (상단) */}
                                            {todos.length > 0 && hasBoth && (
                                                <div className="monthly-section-label">{t('monthly.todoLabel')}</div>
                                            )}
                                            {todos.map((schedule) => (
                                                <div
                                                    key={schedule.schedule_id}
                                                    className={`monthly-schedule-item ${schedule.completed ? 'completed' : ''}`}
                                                    onContextMenu={(e) => onTodoContextMenu(e, schedule)}
                                                    onDoubleClick={(e) => onTodoDoubleClick(e, schedule)}
                                                    draggable={!targetUserId}
                                                    onDragStart={(e) => handleTodoDragStart(e, schedule)}
                                                    onDragEnd={handleTodoDragEnd}
                                                    onTouchStart={(e) => handleItemTouchStart(e, schedule, 'todo')}
                                                    onTouchEnd={() => {
                                                        if (touchTimerRef.current) {
                                                            clearTimeout(touchTimerRef.current);
                                                            touchTimerRef.current = null;
                                                        }
                                                    }}
                                                    style={{
                                                        backgroundColor: schedule.study_subject?.color || '#ccc',
                                                        color: getContrastColor(schedule.study_subject?.color || '#ccc'),
                                                        '--todo-color': schedule.study_subject?.color || '#1976d2',
                                                        cursor: !targetUserId ? 'grab' : 'default'
                                                    }}
                                                    title={`${schedule.study_subject?.subjectname}${schedule.special_text ? ' - ' + schedule.special_text : ''}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={!!schedule.completed}
                                                        onChange={() => onTodoToggle(schedule.schedule_id, !schedule.completed)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <span className="monthly-item-text">
                                                        {schedule.special_text
                                                            ? `[${schedule.study_subject?.subjectname}] ${schedule.special_text}`
                                                            : schedule.study_subject?.subjectname}
                                                    </span>
                                                </div>
                                            ))}
                                            {/* 스케줄 섹션 (하단) */}
                                            {timed.length > 0 && hasBoth && (
                                                <div className="monthly-section-label with-divider">{t('monthly.scheduleLabel')}</div>
                                            )}
                                            {timed.map((schedule) => (
                                                <div
                                                    key={schedule.schedule_id}
                                                    className="monthly-schedule-item"
                                                    onContextMenu={(e) => onScheduleContextMenu && onScheduleContextMenu(e, schedule)}
                                                    onDoubleClick={(e) => onScheduleDoubleClick && onScheduleDoubleClick(e, schedule)}
                                                    onTouchStart={(e) => handleItemTouchStart(e, schedule, 'timed')}
                                                    onTouchEnd={() => {
                                                        if (touchTimerRef.current) {
                                                            clearTimeout(touchTimerRef.current);
                                                            touchTimerRef.current = null;
                                                        }
                                                    }}
                                                    style={{
                                                        backgroundColor: schedule.study_subject?.color || '#ccc',
                                                        color: getContrastColor(schedule.study_subject?.color || '#ccc'),
                                                        cursor: 'pointer'
                                                    }}
                                                    title={`${schedule.study_subject?.subjectname} (${schedule.scheduled_time}${t('minutes')})`}
                                                >
                                                    <span className="monthly-item-text">
                                                        {schedule.special_text
                                                            ? `[${schedule.study_subject?.subjectname}] ${schedule.special_text}`
                                                            : schedule.study_subject?.subjectname}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default MonthlyCalendar;
